import { useEffect, useRef, useState, type ReactNode, useMemo } from 'react';
import type { Account, AiTag, MessageSummary } from '@momomail/shared';
import { avatarColor, displayName, initial, listSep, shortDate } from '../lib/format';
import { newAtTop } from '../lib/motion';
import { AiRowButtons } from './AiButtons';
import { Icon } from './Icon';
import { Popover } from './Overlay';
import { t } from '../lib/i18n';

export type ListFilter = 'all' | 'unread' | 'flagged';

export function Avatar({ name, address, size = 36 }: { name: string; address: string; size?: number }) {
  return (
    <span className="avatar" style={{ '--av': avatarColor(address || name), width: size, height: size, fontSize: size * 0.44 } as React.CSSProperties} aria-hidden>
      {initial({ name, address })}
    </span>
  );
}

export function MessageList({
  leaving,
  title,
  subtitle,
  messages,
  hasMore,
  autoMore,
  loading,
  error,
  selectedId,
  filter,
  search,
  accounts,
  showAccount,
  sentView,
  emptyHint,
  topSlot,
  onFilter,
  onSearch,
  onSelect,
  onOpen,
  onToggleFlag,
  onLoadMore,
  onMarkAllRead,
  onMarkAllReadOnServer,
  unreadTotal,
  onRetry,
  searchRef,
}: {
  /** Rows playing their leave animation (they slide out and close up, then the list drops them). */
  leaving?: ReadonlySet<number>;
  title: string;
  subtitle: string;
  messages: MessageSummary[];
  hasMore: boolean;
  /** Auto-load on scrolling to the bottom; when false, a button at the bottom manually loads older mail. */
  autoMore: boolean;
  loading: boolean;
  error: string | null;
  selectedId: number | null;
  filter: ListFilter;
  search: string;
  accounts: Map<string, Account>;
  showAccount: boolean;
  /** Sent / Drafts: the list shows recipients instead of senders. */
  sentView: boolean;
  emptyHint: ReactNode;
  topSlot?: ReactNode;
  onFilter: (f: ListFilter) => void;
  onSearch: (q: string) => void;
  onSelect: (m: MessageSummary) => void;
  onOpen: (m: MessageSummary) => void;
  onToggleFlag: (m: MessageSummary) => void;
  onLoadMore: () => void;
  onMarkAllRead: () => void;
  /** Mark the whole folder on the server as read; not available in search results (null). */
  onMarkAllReadOnServer: (() => void) | null;
  /** Unread count for the whole list (not just the loaded page); null if unknown. */
  unreadTotal: number | null;
  onRetry: () => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
}) {
  const [readMenu, setReadMenu] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);

  // Auto-load the next page on scrolling to the bottom
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || !autoMore) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && !loading && onLoadMore(), { root: scroller.current, rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, autoMore, loading, onLoadMore]);

  // Scroll the selected message into view
  useEffect(() => {
    if (selectedId == null) return;
    scroller.current?.querySelector<HTMLElement>(`[data-id="${selectedId}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const anyUnread = (unreadTotal ?? 0) > 0 || messages.some((m) => !m.seen || m.threadUnread > 0);

  // New mail arriving at the top of the same list eases in; the first load, paging and switching lists don't animate
  const shown = useRef<{ title: string; ids: number[] }>({ title, ids: [] });
  const fresh = useMemo(() => {
    const prev = shown.current.title === title ? shown.current.ids : [];
    return new Set(newAtTop(prev, messages.map((m) => m.id)));
  }, [messages, title]);
  useEffect(() => {
    shown.current = { title, ids: messages.map((m) => m.id) };
  }, [messages, title]);

  return (
    <section className="pane list" aria-label={t('邮件列表')}>
      <header className="list__head">
        <div className="list__title-row">
          <div className="list__titles">
            <h1 className="list__title">{title}</h1>
            <span className="list__subtitle">{subtitle}</span>
          </div>
          <span className="split list__read">
            <button className="btn btn--small btn--quiet split__main" onClick={onMarkAllRead} disabled={!anyUnread} title={t('列表里的邮件全部标为已读（Shift+I）')}>
              <Icon name="checks" size={15} />
              {t('全部已读')}
            </button>
            {onMarkAllReadOnServer && (
              <button className="btn btn--small btn--quiet split__more" data-popover-anchor aria-label={t('更多已读选项')} aria-haspopup="menu" aria-expanded={readMenu} onClick={() => setReadMenu((v) => !v)}>
                <Icon name="down" size={14} />
              </button>
            )}
            <Popover open={readMenu} onClose={() => setReadMenu(false)} align="right" className="read-menu">
              <button role="menuitem" className="popover__item" disabled={!anyUnread} onClick={() => { setReadMenu(false); onMarkAllRead(); }}>
                <Icon name="checks" size={15} />
                <span>
                  {t('列表里的全部已读')}
                  <small className="popover__hint">{t('已经下载到馍馍邮箱的邮件')}</small>
                </span>
                <kbd>Shift I</kbd>
              </button>
              <button role="menuitem" className="popover__item" onClick={() => { setReadMenu(false); onMarkAllReadOnServer?.(); }}>
                <Icon name="mailOpen" size={15} />
                <span>
                  {t('服务器上全部已读')}
                  <small className="popover__hint">{t('包括还没下载的老邮件')}</small>
                </span>
              </button>
            </Popover>
          </span>
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input
            ref={searchRef}
            type="search"
            placeholder={t('搜索主题、联系人、正文…')}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.stopPropagation();
                onSearch('');
                e.currentTarget.blur();
              }
              if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                e.currentTarget.blur();
                if (messages[0]) onSelect(messages[0]);
              }
            }}
            aria-label={t('搜索邮件')}
            data-afford-key="mail.search"
            data-afford-describe="Words to search for: subject, sender, body (the mail server is searched too). Write them in: the list shows what is found (clear it for the mailbox again)"
          />
          {search && (
            <button className="bare search__clear" aria-label={t('清除搜索')} onClick={() => onSearch('')}>
              <Icon name="x" size={14} />
            </button>
          )}
          {!search && <kbd className="search__kbd">/</kbd>}
        </label>
        <div className="segmented list__filter" role="group" aria-label={t('筛选')}>
          {(
            [
              ['all', '全部'], // i18n-ok
              ['unread', '未读'], // i18n-ok
              ['flagged', '星标'], // i18n-ok
            ] as const
          ).map(([key, label]) => (
            <button key={key} aria-pressed={filter === key} onClick={() => onFilter(key)}>
              {t(label)}
            </button>
          ))}
        </div>
      </header>

      <div
        className="list__scroll"
        ref={scroller}
        role="listbox"
        aria-label={title}
        tabIndex={-1}
        data-afford-container="list"
        data-afford-key="mail.list"
        data-afford-describe={`The mail in "${title}", newest first. Press an entry to open it. Only the newest are loaded: older ones come with mail.load_older (at the end of the list), or find them with mail.search`}
      >
        {topSlot}
        {error && (
          <div className="list__state">
            <div className="notice notice--error">{error}</div>
            <button className="btn btn--small" onClick={onRetry}>
              {t('重新加载')}
            </button>
          </div>
        )}
        {!error && loading && messages.length === 0 && <Skeleton />}
        {!error && !loading && messages.length === 0 && <div className="list__empty">{emptyHint}</div>}
        {messages.map((m) => {
          const account = accounts.get(m.accountId);
          const isLeaving = leaving?.has(m.id) ?? false;
          const unread = !m.seen || m.threadUnread > 0;
          const who = sentView ? (m.to.length ? m.to.map((a) => displayName(a)).join(listSep()) : t('(无收件人)')) : displayName(m.from);
          return (
            <div
              key={m.id}
              data-id={m.id}
              data-afford-item={m.id}
              role="option"
              aria-selected={selectedId === m.id}
              tabIndex={-1}
              className={`row ${unread ? 'is-unread' : ''} ${selectedId === m.id ? 'is-selected' : ''} ${isLeaving ? 'is-leaving' : ''} ${fresh.has(m.id) ? 'is-new' : ''}`}
              aria-hidden={isLeaving || undefined}
              onClick={() => !isLeaving && onSelect(m)}
              onDoubleClick={() => onOpen(m)}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/x-momomail-ids', JSON.stringify([m.id]));
                e.dataTransfer.effectAllowed = 'move';
              }}
            >
              <Avatar name={sentView ? (m.to[0]?.name ?? '') : m.from.name} address={sentView ? (m.to[0]?.address ?? '') : m.from.address} />
              <div className="row__main">
                <div className="row__top">
                  {unread && <i className="row__dot" aria-label={t('未读')} />}
                  <span className="row__from">
                    {sentView && <span className="row__to-label">{t('发给')} </span>}
                    {who}
                  </span>
                  {m.threadCount > 1 && <span className="row__count">{m.threadCount}</span>}
                  <span className="row__date">{shortDate(m.date)}</span>
                </div>
                <div className="row__subject">
                  {m.draft && <span className="row__draft">{t('草稿')}</span>}
                  {m.aiTag && <AiTagChips tag={m.aiTag} />}
                  {m.subject || t('(无主题)')}
                </div>
                <div className="row__bottom">
                  {/* With an AI tag, its one-line summary says more than the opening words */}
                  <span className="row__snippet" title={m.aiTag?.summary ? m.snippet : undefined}>
                    {m.aiTag?.summary || m.snippet || ' '}
                  </span>
                  {showAccount && account && <i className="row__acc" style={{ background: account.color }} title={account.email} />}
                  {m.hasAttachments && <Icon name="clip" size={14} className="row__clip" />}
                  {m.answered && <Icon name="reply" size={14} className="row__clip" />}
                  <button
                    className={`bare row__star ${m.flagged ? 'is-on' : ''}`}
                    data-afford-key="star"
                    aria-label={m.flagged ? t('取消星标') : t('加星标')}
                    aria-pressed={m.flagged}
                    title={m.flagged ? t('取消星标（S）') : t('加星标（S）')}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFlag(m);
                    }}
                  >
                    <Icon name="star" size={16} fill={m.flagged} />
                  </button>
                </div>
                {/* The AI's buttons ("加入日历", "退订"): usable without opening the mail */}
                <AiRowButtons message={m} />
              </div>
            </div>
          );
        })}
        {hasMore &&
          (autoMore || loading ? (
            <div ref={sentinel} className="list__more">
              <span className="spinner" /> {t('加载更早的邮件…')}
            </div>
          ) : (
            <div className="list__more">
              <button className="btn btn--small btn--quiet" onClick={onLoadMore} data-afford-key="mail.load_older" data-afford-describe="Loads older mail from the server into this list">
                <Icon name="refresh" size={14} /> {t('在服务器上更早的邮件里找')}
              </button>
            </div>
          ))}
      </div>
    </section>
  );
}

function Skeleton() {
  return (
    <div aria-label={t('加载中')} aria-busy>
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="row row--skeleton">
          <span className="avatar sk" />
          <div className="row__main">
            <span className="sk sk--line" style={{ width: `${40 + ((i * 17) % 30)}%` }} />
            <span className="sk sk--line" style={{ width: `${70 + ((i * 13) % 25)}%` }} />
            <span className="sk sk--line sk--thin" style={{ width: `${55 + ((i * 11) % 35)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** A tag's color: the same name always gets the same one (palette in app.css, .ai-tag--c0 … c5). */
export function labelTone(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return h % 6;
}

/**
 * The AI's tags in front of the subject: the system's (important / junk, needs reply) first, then the user's own tags.
 * Normal and low importance show nothing — they're the everyday case.
 */
export function AiTagChips({ tag }: { tag: AiTag }) {
  return (
    <span className="ai-tags">
      {tag.level === 'important' && <span className="ai-tag ai-tag--urgent">{t('重要')}</span>}
      {tag.level === 'junk' && <span className="ai-tag ai-tag--junk">{t('垃圾')}</span>}
      {tag.needsReply && <span className="ai-tag ai-tag--reply">{t('待回复')}</span>}
      {tag.labels.map((l) => (
        <span key={l} className={`ai-tag ai-tag--c${labelTone(l)}`}>
          {l}
        </span>
      ))}
    </span>
  );
}
